<template>
    <div class="log">

        <div v-if="log.timeslots.length == 0">
            <card>
                <p>{{ $t('sessions.empty-log-notice') }}</p>
            </card>
        </div>
        
        <div v-else>
            <div :class="{'pb-2': active, 'pb-4': !active}" class="text-center">
                <base-button type="white mr-1" size="sm" @click="expandAll">
                    <i class="fas fa-chevron-down"></i>
                    {{ $t('common.expand-all') }}
                </base-button>
                
                <base-button type="white" size="sm" @click="collapseAll">
                    <i class="fas fa-chevron-up"></i>
                    {{ $t('common.collapse-all') }}
                </base-button>
            </div>
        </div>

        <div ref="timeslots" class="timeslots-ref" v-if="observations">
            <collapse class="timeslots pb-3" ref="timeslots-collapse">

                <div v-for="(timeslot, index) in visibleTimeslots" :key="index" :class="{'pt-4': !active}">

                    <features v-if="stats"
                        :registration="registration"
                        :degree="degree"
                        :simulation="simulation"
                        :session="session"
                        :stats="stats"
                        :timeslot="timeslot"
                        :features="features"
                        @refresh="$emit('refresh')"
                        @createOrUpdateNote="$refs['modals'].createOrUpdateNote($event)"
                        @createOrUpdatePicture="$refs['modals'].createOrUpdatePicture($event)"
                        @createIssue="$refs['modals'].createIssue($event)"
                    >
                    </features>

                    <collapse-item-card
                        :id="'timeslot-'+timeslot.id"
                        :ref="'timeslot-'+timeslot.id"
                        :active-default="!defaultTimeslotCollapsing(timeslot.id)"
                        :class="timeslotStyle(timeslot)"
                        width-style="width-80"
                        @change="timeslotCollapsed(timeslot.id, $event)"
                    >
                    
                        <template slot="title">
                            <div class="row align-items-center pl-4">
                                <div class="col" style="flex: 0 0 80px">
                                    <h5 class="mb-0 float-left" style="font-size:1.1rem;font-weight:700">
                                        {{ timeslot.is_instruction && !timeslot.show_time ? 'INST' : timeslot.time }}
                                    </h5>
                                </div>
                                <div class="col pl-0">
                                    <h6 class="font-weight-normal text-sm text-uppercase mb-0">
                                        {{ timeslot.instructor_summary }}
                                    </h6>
                                </div>
                            </div>
                        </template>

                        <template>
                            <div class="timeslot tinymce pl-4 pb-1" v-html="timeslot.description" v-if="timeslot.description"></div>

                            <div class="pb-3">
                                <div v-for="(item, index) in filteredItems(timeslot)" :key="index">

                                    <div class="group pl-0 mr-2 ml-4 mt-3" v-if="item.group">
                                        <table width="100%">
                                            <tr class="group-flights" v-if="groupType(item) == 'approach'">
                                                <td></td>
                                                <td class="yes-no" width="95"
                                                    v-for="(flight, colIndex) in groupColumns(item)" :key="colIndex"
                                                >
                                                    <div class="flight text-center pb-1" v-if="session && groupType(item) == 'approach'">
                                                        <i class="fa fa-plane"></i> {{ flight }}
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr class="group-title">
                                                <td class="p-1">
                                                    <h3>
                                                        {{ item.title ? item.title : 'Actions' }}
                                                    </h3>
                                                </td>
                                                <td class="yes-no" width="95"
                                                    v-for="(flight, colIndex) in groupColumns(item)" :key="colIndex"
                                                >
                                                    <button-radio-group 
                                                        :options="observationOptions"
                                                        button-classes="btn-secondary btn-sm mb-1 mt-1"
                                                        class="d-inline text-center"
                                                        @change="selectColumn(timeslot, item, $event, colIndex)"
                                                        v-if="session && features.canEdit()"
                                                    >
                                                    </button-radio-group>
                                                    <div class="flight text-center text-dark" v-if="!session && groupType(item) == 'approach'">
                                                        <i class="fa fa-plane"></i> {{ flight }}
                                                    </div>
                                                </td>
                                            </tr>
                                        </table>
                                    </div>

                                    <div class="items mr-2 ml-4" v-else>
                                        <table width="100%">
                                            <tr class="item">
                                                <td class="type py-2" width="40">
                                                    <el-popover placement="top">
                                                        <a slot="reference" href="#" @click.prevent="">
                                                            <i :class="types[item.type].icon+' text-'+types[item.type].color"
                                                                style="font-size:1.6rem;"
                                                            ></i>
                                                        </a>
                                                        <h5 class="text-center mb-0">
                                                            {{ types[item.type].name[lang] }}
                                                        </h5>
                                                    </el-popover>
                                                </td>
                                                <td class="description py-2">
                                                    <div :class="' pl-3 border-left border-'+types[item.type].color">
                                                        <div class="tinymce" v-html="item.description"></div>
                                                    </div>

                                                    <div class="mb-2" v-if="item.competency_ids && item.competency_ids.length && filters.showCompetencies">
                                                        <span v-for="competencyId in item.competency_ids" :key="competencyId">
                                                            <badge type="primary" class="mr-1" v-if="competencyExists(competencyId)">
                                                                {{ competencyName(competencyId) }}
                                                            </badge>
                                                        </span>
                                                    </div>
                                                </td>
                                                <td class="py-2" :class="{'yes-no': itemType(timeslot, item) == 'yesno' || itemType(timeslot, item) == 'approach'}" width="95"
                                                    v-for="(flight, index) in itemColumns(timeslot, item)" :key="index"
                                                >
                                                    <button-radio-group 
                                                        :options="observationOptions"
                                                        button-classes="btn-secondary btn-sm"
                                                        v-model="observations[timeslot.id].items[item.id].value"
                                                        :alt-value="harmonizationValue(timeslot.id, item.id)"
                                                        :unselectable="true"
                                                        :unselected-value="null"
                                                        class="d-inline text-center"
                                                        :disabled="!features.canEdit()"
                                                        v-if="itemType(timeslot, item) == 'yesno'"
                                                    >
                                                    </button-radio-group>

                                                    <button-radio-group 
                                                        :options="observationOptions"
                                                        button-classes="btn-secondary btn-sm"
                                                        v-model="observations[timeslot.id].items[item.id].value[index]"
                                                        :alt-value="harmonizationValue(timeslot.id, item.id, index)"
                                                        :unselectable="true"
                                                        :unselected-value="null"
                                                        class="d-inline"
                                                        :disabled="!features.canEdit()"
                                                        v-if="itemType(timeslot, item) == 'approach'"
                                                    >
                                                    </button-radio-group>

                                                    <div class="text-right" v-if="itemType(timeslot, item) == 'error'">
                                                        <base-switch
                                                            v-model="observations[timeslot.id].items[item.id].value"
                                                            :alt-value="harmonizationValue(timeslot.id, item.id)"
                                                            :disabled="!features.canEdit()"
                                                        ></base-switch>
                                                    </div>
                                                </td>
                                            </tr>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div class="pb-3 pr-3 text-right" v-if="timeslot.is_instruction && role == 'instructor'">

                                <base-button type="white" size="sm" @click="collapseItem(timeslot.id)">
                                    <i class="fas fa-chevron-up mr-1"></i>
                                    {{ $t('common.collapse') }}
                                </base-button>
                            </div>

                            <div class="pb-3 pr-3 text-right" v-if="!timeslot.is_instruction && role == 'instructor'">

                                <base-button type="white" size="sm" @click="harmonize(timeslot.id)"
                                    v-if="session
                                        && !session.sync
                                        && session.tester2_id
                                        && !harmonization[timeslot.id]
                                        && features.canEdit()
                                    "
                                >
                                    <i class="fas fa-sync mr-1"></i>
                                    {{ $t('sessions.harmonize') }}
                                </base-button>

                                <base-button type="white" size="sm" @click="harmonizeStop(timeslot.id)"
                                    v-if="session
                                        && session.tester2_id
                                        && harmonization[timeslot.id]
                                        && features.canEdit()
                                    "
                                >
                                    <i class="fas fa-sync mr-1"></i>
                                    {{ $t('sessions.harmonize-stop') }}
                                </base-button>

                                <base-button type="white" size="sm" @click="checkCompletion(timeslot.id)"
                                    v-if="session
                                        && isTimeslotCompletionForced(timeslot.id)
                                        && features.canEdit()
                                    "
                                >
                                    <i class="far fa-check-square mr-1"></i>
                                    {{ $t('sessions.check-completion') }}
                                </base-button>

                                <base-button type="white" size="sm" @click="markCompleted(timeslot.id)"
                                    v-if="session
                                        && !isTimeslotCompletionForced(timeslot.id)
                                        && !isTimeslotCompleted(timeslot.id)
                                        && features.canEdit()
                                    "
                                >
                                    <i class="far fa-check-square mr-1"></i>
                                    {{ $t('sessions.mark-completed') }}
                                </base-button>

                                <base-button type="white" size="sm" @click="collapseItem(timeslot.id)">
                                    <i class="fas fa-chevron-up mr-1"></i>
                                    {{ $t('common.collapse') }}
                                </base-button>
                            </div>
                        </template>
                    </collapse-item-card>
                </div>
            </collapse>
        </div>
        
        <div class="text-center mb-3 mt--4" v-if="showLoadingMore">
            <loading-more @click.prevent="loadMore"></loading-more>
        </div>

        <div style="padding-top:200px;padding-bottom:200px;">
            <h3 class="text-muted text-center">SOCRATES</h3>
        </div>

        <portal to="modals">

            <modals ref="modals"
                :modals="modals"
                :registration="registration"
                :simulation="simulation"
                :session="session"
                :features="features"
                :role="role"
                @refresh="$emit('refresh')"
            ></modals>

        </portal>
    </div>
</template>

<script>
    import ModalsController from '@trax/classes/ModalsController'
    import Features from './TimeslotFeatures'
    import Modals from './TimeslotModals'
    import Filters from './Filters'

    export default {

        components: {
            Features, Filters, Modals
        },

        props: {

            log: {
                type: Object,
                required: true
            },

            registration: {
                type: Object,
                required: true
            },

            degree: {
                type: Object,
                required: true
            },

            simulation: {
                type: Object,
                required: true
            },

            session: {
                type: Object,
                default: null
            },
            
            competencies: {
                type: Object,
                default: null
            },
                        
            types: {
                type: Object,
                required: true
            },

            stats: {
                type: Object,
                default: null
            },
            
            initialObservations: {
                type: Object,
                default: null
            },

            lang: {
                type: String,
                default: "en"
            },

            filters: {
                type: Object,
                default: {}
            },

            features: {
                default: null
            },

            role: {
                type: String,
                default: 'instructor'
            },
        },

        data() {
            return {
                modals: new ModalsController([
                    //'confirmStop',
                    //'updateNote',
                    'editNote',
                    'editPicture',
                    //'editPictureFile', 
                    //'confirmDeleteFeedback',
                    'editIssue', 'updateIssue', 'confirmDeleteIssue'
                ]),
                visibleTimeslotsCount: 10,
                defaultCollapsing: false,
                timeslotsCollapsing: null,
                observationOptions: [
                    { value: 'yes', label: 'Y' },
                    { value: 'no', label: 'N' },
                    { value: '-', label: '-' },
                ],
                timeslotIdToScroll: null,
                firstTimeslot: null,
                lastTimeslot: null,
                updatingTimeslotsVisibility: false,
                timeslotsWithProgress: {},
                harmonization: {},
                observations: null,
            }
        },

        created() {
            this.modals.init(this, 'modals')
            this.restoreCollapsing()
        },

        mounted() {
            this.onScroll()
            this.$nextTick(function() {
                window.addEventListener('scroll', this.onScroll);
            });        
        },

        beforeDestroy() {
            window.removeEventListener('scroll', this.onScroll);
        },

        computed: {

            positions() {
                let positions = [{
                    id: 1,
                    name: this.degree.simulation_role_1_name[this.lang]
                }]
                if (this.degree.simulation_role_2) {
                    positions.push({
                        id: 2,
                        name: this.degree.simulation_role_2_name[this.lang]
                    })
                }
                return positions
            },

            active() {
                return true
            },

            filteredTimeslots() {
                return this.log.timeslots.filter(timeslot => {
                    return this.filteredItems(timeslot).length > 0 || timeslot.is_instruction
                })
            },

            visibleTimeslots() {
                return this.filteredTimeslots.slice(0, this.visibleTimeslotsCount)
            },

            showLoadingMore() {
                return this.visibleTimeslotsCount < this.filteredTimeslots.length
            },
        },

        watch: {
            initialObservations: {
                handler() {
                    this.initObservations()
                    this.initTimeslotsStatus()
                    this.updateTimeslotsProgress()
                },
                immediate: true,
            },

            observations: {
                handler() {
                    this.updateTimeslotsProgress()
                },
                deep: true,
            },

            timeslotsWithProgress() {
                // When an input is hitted, the observations watcher is triggered, which leads to the progress update.
                // Then, the progress may be changed in the observations object, which triggers again the observations watcher,
                // leading to 2 savings.
                // We want to avoid that, so we react only to the timeslotsWithProgress watcher in order to save.
                this.saveObservations()
            },

            firstTimeslot() {
                this.updateTimeslotsVisibility()
            },

            lastTimeslot() {
                this.updateTimeslotsVisibility()
            },
        },

        methods: {

            competencyExists(competencyId) {
                return this.competencies[competencyId] !== undefined
            },

            competencyName(competencyId) {
                return this.competencies[competencyId]['name_' + this.degree.lang]
            },

            // --- Observations struct ---

            isTimeslotCompletionForced(timeslotId) {
                return this.observations[timeslotId].mode == 'forced'
            },

            checkCompletion(timeslotId) {
                this.observations[timeslotId].mode = 'computed'
                this.updateTimeslotsProgress()
            },

            markCompleted(timeslotId) {
                this.observations[timeslotId].mode = 'forced'
                this.updateTimeslotsProgress()
            },

            saveObservations() {
                axios.post('/socrates/api/front/dashboard/data/sessions/'+this.session.id+'/observation', {
                    data: this.observations
                })
                .then(resp => {
                    this.$emit('observationChanged')
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            },

            initObservations() {
                this.observations = {}
                for (const timeslot of this.log.timeslots) {
                    if (timeslot.is_instruction) {
                        continue
                    }
                    if (!this.initialObservations) {
                        continue
                    }
                    if (this.initialObservations[timeslot.id]) {
                        this.observations[timeslot.id] = this.initialObservations[timeslot.id]
                    } else {
                        Vue.set(this.observations, timeslot.id, {
                            items: {},
                            mode: 'computed',
                            progress: 'not-attempted',
                        })
                    }
                    this.initObservationItems(this.observations[timeslot.id], this.initialObservations[timeslot.id], timeslot)
                }
            },

            initObservationItems(updatedObservation, originalObservation, timeslot) {
                let group = null
                for (const item of timeslot.items) {
                    if (item.group) {
                        group = item
                        continue
                    }
                    if (!group) {
                        continue
                    }
                    if (originalObservation && originalObservation.items[item.id]) {
                        updatedObservation.items[item.id] = originalObservation.items[item.id]
                        continue
                    }
                    if (item.type == 'critical_error' || item.type == 'fatal_error') {
                        Vue.set(updatedObservation.items, item.id, {
                            type: 'error',
                            value: false,
                            group_id: group.id,
                            competency_ids: item.competency_ids,
                        })
                    } else if (group.flights.length > 0) {
                        Vue.set(updatedObservation.items, item.id, {
                            type: 'approach',
                            value: group.flights.map(flight => null),
                            flights: group.flights,
                            group_id: group.id,
                            competency_ids: item.competency_ids,
                        })
                    } else {
                        Vue.set(updatedObservation.items, item.id, {
                            type: 'yesno',
                            value: null,
                            group_id: group.id,
                            competency_ids: item.competency_ids,
                        })
                    }
                }
                // Remove items which were deleted from the log.
                for (const itemId of Object.keys(updatedObservation.items)) {
                    if (!timeslot.items.find(item => item.id == itemId)) {
                        delete updatedObservation.items[itemId]
                    }
                }
            },

            // --- Timeslots status struct ---

            initTimeslotsStatus() {
                for (const timeslot of this.log.timeslots) {
                    this.timeslotsWithProgress[timeslot.id] = {
                        id: timeslot.id,
                        is_instruction: timeslot.is_instruction,
                        show_time: timeslot.show_time,
                        time: timeslot.time,
                        progress: timeslot.is_instruction || !this.observations[timeslot.id] ? null : this.observations[timeslot.id].progress,
                    }
                }
                this.$emit('progressChanged', {...this.timeslotsWithProgress})
            },

            // --- Progress ---

            isTimeslotCompleted(timeslotId) {
                return this.timeslotProgress(timeslotId) == 'completed'
            },

            timeslotProgress(timeslotId) {
                return this.timeslotsWithProgress[timeslotId].progress
            },

            updateTimeslotsProgress() {
                if (!this.session) {
                    return
                }
                for (const timeslotId of Object.keys(this.timeslotsWithProgress)) {
                    let timeslot = this.timeslotsWithProgress[timeslotId]
                    if (!timeslot.is_instruction) {
                        timeslot.progress = this.computeTimeslotProgress(timeslot.id)
                        this.observations[timeslot.id].progress = timeslot.progress
                    }
                }
                this.timeslotsWithProgress = {...this.timeslotsWithProgress}
                this.$emit('progressChanged', this.timeslotsWithProgress)
            },

            computeTimeslotProgress(timeslotId) {

                if (this.isTimeslotCompletionForced(timeslotId)) {
                    return 'completed'
                }

                let started = false
                let completed = true

                // No item in the timeslot.
                if (Array.isArray(this.observations[timeslotId].items)) {
                    return 'not-attempted'
                }

                for (let itemId of Object.keys(this.observations[timeslotId].items)) {
                    let item = this.observations[timeslotId].items[itemId]
                    if (item.type == 'approach') {
                        for (let index in item.flights) {
                            started = started || item.value[index]
                            completed = completed && item.value[index]
                        }
                    }
                    if (item.type == 'yesno') {
                        started = started || item.value
                        completed = completed && item.value
                    }
                    if (item.type == 'error') {
                        started = started || item.value
                    }
                }
                
                if (!started) {
                    return 'not-attempted'
                }
                if (started && !completed) {
                    return 'incomplete'
                }
                if (completed) {
                    return 'completed'
                }
            },

            timeslotStyle(timeslot) {
               if (timeslot.is_instruction) {
                    return 'timeslot-instruction'
               } 
                return {
                    'not-attempted': '',
                    'incomplete': 'timeslot-warning',
                    'completed': 'timeslot-success',
                }[this.timeslotProgress(timeslot.id)]
            },

            // --- Visibility ---

            updateTimeslotsVisibility() {
                if (this.updatingTimeslotsVisibility) {
                    return
                }
                this.updatingTimeslotsVisibility = true
                setTimeout(() => {
                    this.updateTimeslotsVisibilityNow()
                }, 250)
            },

            updateTimeslotsVisibilityNow() {
                let timeslotsVisibility = {}
                let firstTimeslotFound = false
                let lastTimeslotFound = false
                for (let timeslot of this.log.timeslots) {
                    firstTimeslotFound = firstTimeslotFound || timeslot.id == this.firstTimeslot
                    timeslotsVisibility[timeslot.id] = firstTimeslotFound && !lastTimeslotFound
                    lastTimeslotFound = lastTimeslotFound || timeslot.id == this.lastTimeslot
                }
                this.updatingTimeslotsVisibility = false
                this.$emit('visibilityChanged', timeslotsVisibility)
            },


            // --- Harmonization struct ---

            harmonizationValue(timeslotId, itemId, index = null) {
                return this.harmonization[timeslotId]
                    ? (index !== null
                        ? this.harmonization[timeslotId][itemId].value[index]
                        : this.harmonization[timeslotId][itemId].value
                    )
                    : null
            },

            harmonize(timeslotId) {
                axios.get('/socrates/api/front/dashboard/data/sessions/'+this.session.id+'/harmonization/timeslots/'+timeslotId)
                .then(resp => {
                    // Don't assign empty harmonization. Delete it.
                    if (this.harmonizationIsEmpty(resp.data.data, timeslotId)) {
                        delete this.harmonization[timeslotId]
                    } else {
                        this.harmonization[timeslotId] = resp.data.data
                        this.harmonization = {...this.harmonization}
                    }
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            },

            harmonizationIsEmpty(data, timeslotId) {
                // Empty harmonization data is an empty array. Otherwize, it is an object.
                if (Array.isArray(data)) {
                    return true
                }
                // Check if there is an item with a value.
                for (const itemId of Object.keys(data)) {
                    const item = data[itemId]
                    if (item.type == 'approach' && item.value.filter(val => val !== null).length) {
                        // One of the flight values is not null.
                        return false
                    }
                    if (item.type == 'yesno' && item.value !== null) {
                        // The value is not null (yes, no or -).
                        return false
                    }
                    if (item.type == 'error' && item.value) {
                        // The error value is true.
                        return false
                    }
                }
                return true
            },

            harmonizeStop(timeslotId) {
                delete this.harmonization[timeslotId]
                this.harmonization = {...this.harmonization}
            },

            // --- Form presentation and interactivity ---

            selectColumn(timeslot, group, value, flightIndex) {
                // Keep only the right items.
                let itemIds = Object.keys(this.observations[timeslot.id].items).filter(itemId => {
                    return this.observations[timeslot.id].items[itemId].group_id == group.id
                        && (this.observations[timeslot.id].items[itemId].type == 'approach' || this.observations[timeslot.id].items[itemId].type == 'yesno')
                })

                // Check if they are all selected with the targetted value.
                let allChecked = true
                for (let itemId of itemIds) {
                    if (this.observations[timeslot.id].items[itemId].type == 'yesno' && this.observations[timeslot.id].items[itemId].value != value) {
                        allChecked = false
                        break
                    }
                    if (this.observations[timeslot.id].items[itemId].type == 'approach' && this.observations[timeslot.id].items[itemId].value[flightIndex] != value) {
                        allChecked = false
                        break
                    }
                }

                // Select or unselect all.
                for (let itemId of itemIds) {
                    if (this.observations[timeslot.id].items[itemId].type == 'yesno') {
                        this.observations[timeslot.id].items[itemId].value = allChecked ? null : value
                    }
                    if (this.observations[timeslot.id].items[itemId].type == 'approach') {
                        this.observations[timeslot.id].items[itemId].value[flightIndex] = allChecked ? null : value
                    }
                }
                this.observations = {...this.observations}  // For reactivity
            },

            itemType(timeslot, item) {
                if (!this.observations[timeslot.id]) {
                    return ''
                }
                return this.observations[timeslot.id].items[item.id]
                    ? this.observations[timeslot.id].items[item.id].type
                    : ''
            },

            itemColumns(timeslot, item) {
                let type = this.itemType(timeslot, item)
                if (type == 'approach') {
                    return this.observations[timeslot.id].items[item.id].flights
                }
                if (type == 'yesno' || type == 'error') {
                    return [{}]
                }
                return []
            },

            groupType(group) {
                return group.flights.length ? 'approach' : 'yesno'
            },

            groupColumns(group) {
                if (this.groupType(group) == 'approach') {
                    return group.flights
                }
                return [{}]
            },

            // --- Collapsing ---

            restoreCollapsing() {
                let logCollapse = this.$auth.getUiConfig('log_collapse')
                if (logCollapse && logCollapse.id == this.log.id) {
                    this.timeslotsCollapsing = logCollapse.timeslots
                    this.defaultCollapsing = logCollapse.default
                } else {
                    this.timeslotsCollapsing = {}
                    this.rememberCollapsing()
                }
            },
  
            rememberCollapsing() {
                let collapsing = {
                    id: this.log.id,
                    default: this.defaultCollapsing,
                    timeslots: this.timeslotsCollapsing,
                }
                this.$auth.setUiConfig(collapsing, 'log_collapse')
            },

            defaultTimeslotCollapsing(id) {
                return this.timeslotsCollapsing[id] !== undefined ? this.timeslotsCollapsing[id] : this.defaultCollapsing
            },

            timeslotCollapsed(id, active) {
                this.timeslotsCollapsing[id] = !active
                this.rememberCollapsing()
            },

            expandAll() {
                this.$refs['timeslots-collapse'].expandAll();
                this.defaultCollapsing = false
                this.timeslotsCollapsing = {}
                this.rememberCollapsing()
            },

            collapseAll() {
                this.$refs['timeslots-collapse'].collapseAll();
                this.defaultCollapsing = true
                this.timeslotsCollapsing = {}
                this.rememberCollapsing()
            },

            collapseItem(timeslotId) {
                this.$refs['timeslots-collapse'].collapseItem('timeslot-' + timeslotId)
                this.timeslotsCollapsing[timeslotId] = true
                this.rememberCollapsing()
            },

            // --- Filtering ---
            
            filteredItems(timeslot) {
                let items = timeslot.items.filter(item => {

                    //const alwaysVisible = item.type == 'position_change' || item.group

                    const byType = this.filters.selectedTypes.length == 0
                        || this.filters.selectedTypes.includes(item.type)

                    const byCompetency = this.filters.selectedCompetencies.length == 0
                        || this.filters.selectedCompetencies.filter(x => item.competency_ids.includes(parseInt(x))).length > 0

                    /*
                    const byPosition = (this.filters.position1 && item.simulation_role_1)
                        || (this.filters.position2 && item.simulation_role_2)
                    */

                    return item.group || (byType && byCompetency)
                })

                let finalItems = []
                items.forEach((item, index) => {
                    if (!item.group || (items.length > index + 1 && !items[index + 1].group)) {
                        finalItems.push(item)
                    }
                })
                return finalItems
            },

            // --- Scrolling ---

            scrollToTimeslot(timeslotId) {
                this.timeslotIdToScroll = null
                if ($('#timeslot-' + timeslotId).offset() === undefined) {
                    this.timeslotIdToScroll = timeslotId
                    this.loadMore()
                } else {
                    $("html, body").animate({ scrollTop: $('#timeslot-' + timeslotId).offset().top - 30}, 300);
                    /*
                    this.$refs['timeslots-collapse'].expandItem('timeslot-' + timeslotId)
                    this.timeslotsCollapsing[timeslotId] = false
                    this.rememberCollapsing()
                    */
                }
            },

            resumeScrollToTimeslot() {
                if (this.timeslotIdToScroll) {
                    this.scrollToTimeslot(this.timeslotIdToScroll)
                }
            },

            loadMore() {
                if (this.visibleTimeslotsCount < this.filteredTimeslots.length) {
                    this.visibleTimeslotsCount = this.visibleTimeslotsCount + 10
                    this.$nextTick(() => {
                        this.resumeScrollToTimeslot()
                    });
                }
            },

            onScroll() {
                // Check if we need to load more items.
                const timeslotsDiv = this.$refs["timeslots"];
                if (timeslotsDiv) {
                    const timeslotsBottom = timeslotsDiv.getBoundingClientRect().bottom;
                    if (timeslotsBottom < window.innerHeight) {                 
                        this.loadMore()
                    }
                }

                // Determine the ID of the first visible timeslot.
                for (let index in this.log.timeslots) {
                    let timeslot = this.log.timeslots[index]
                    let timeslotDiv = this.$refs['timeslot-'+timeslot.id]
                    if (timeslotDiv) {
                        const timeslotBottom = timeslotDiv[0].$el.getBoundingClientRect().bottom;
                        if (timeslotBottom > 0) {                 
                            this.firstTimeslot = timeslot.id
                            break;
                        }
                    }
                }

                // Determine the ID of the last visible timeslot.
                this.lastTimeslot = this.firstTimeslot
                for (let index in this.log.timeslots) {
                    let timeslot = this.log.timeslots[index]
                    let timeslotDiv = this.$refs['timeslot-'+timeslot.id]
                    if (timeslotDiv) {
                        const timeslotTop = timeslotDiv[0].$el.getBoundingClientRect().top;
                        if (timeslotTop < window.innerHeight) {                 
                            this.lastTimeslot = timeslot.id
                        }
                    }
                }
            },
        }
    }
</script>

<style scoped>

    .log .timeslots-ref {
        margin-top: 2.5rem;
    }
    .log .timeslots >>> .card {
        margin-top: -55px;
        margin-bottom: 40px;
    }
    .log .timeslots >>> .card-header {
        border-bottom: 0px solid;
    }
    .log .timeslots >>> .card {
        border: solid 3px #fff;
    }
    .log .timeslots >>> .card.timeslot-warning {
        border: solid 3px #fb6340;
    }
    .log .timeslots >>> .card.timeslot-success {
        border: solid 3px #2dce89;
    }
    .log .timeslots >>> .card.timeslot-instruction {
        border: thick double #ccc;
    }
    .log .timeslots >>> .card-header:after {
        right: auto;
    }
    .log .timeslots >>> .card-body {
        padding: 0;
    }
    .log .timeslots >>> .card-body tr.el-table__row td {
        padding-top: 0;
        border-bottom: 0px solid;
    }
    .log .timeslots >>> .card-body tr.el-table__row:last-child td {
        padding-bottom: 10px;
    }
    .log .timeslots >>> .type-cell {
        padding-right: 0;
        vertical-align: top;
    }
    .log .timeslots >>> .type-cell .cell i {
        font-size: 1.6rem;
    }
    .log .timeslots >>> .content-cell {
        padding-left: 10px;
    }
    .log .timeslots >>> .content-cell .border-left {
        border-width: 2px !important;
    }

    .log .timeslots >>> .tinymce p {
        font-size: .9rem !important;
    }
    .log .timeslots >>> .item .description .border-left {
        border-width: 2px !important;
    }

    .log .timeslots >>> .group .group-title {
        background-color: #dcdfe6;
        border-radius: 5px;
    }
    .log .timeslots >>> .group .group-title h3 {
        color: #32325d;
        font-size: 1rem;
        margin-bottom: 0;
        text-transform: uppercase;
        font-weight: 700;
        padding-left: 10px;
    }
    .log .timeslots >>> .item td {
        border-top: dashed 1px #DCDFE6;
    }
    .log .timeslots >>> .item td.yes-no {
        padding-bottom: 3px !important;
        border-left: solid 1px #DCDFE6;
    }
    .log .timeslots >>> td.yes-no {
        padding-left: .15rem;
    }
    .log .timeslots >>> .flight {
        font-size: 0.8rem;
    }

</style>
